<script lang="ts">
  import type { WebRequestConfig } from '../types/web-request.interface';
  import InlineSvg from './elements/InlineSvg.svelte';
  import Button from './elements/Button.svelte';
  import { isInstalled, requestInstall } from '../lib/helperExtension';
  import config from '../config/index';
  import { askExtension, ExtentionMethod } from '../lib/messageToExtension';

  export let checked = false;
  export let granted = false;
  export let webRequestConfig: WebRequestConfig = config('WEB_REQUEST_CONFIG');
  export let selectedItemUrl = '';

  let playbackRate = 0.33;

  $: selectedItemHostname = selectedItemUrl ? new URL(selectedItemUrl).hostname : '';

  let template: {
    title: string;
    message?: string;
    button?: {
      label: string;
      onClick: () => Promise<void>;
    };
    buttonSecondary?: {
      label: string;
      onClick: () => void;
    };
  } = null;

  start();

  function setGranted(_granted: boolean, _template?: typeof template) {
    checked = true;
    granted = _granted;

    if (_template) {
      template = _template;
    }
  }

  async function start() {
    const { web, extension } = webRequestConfig;
    const isHelperExtensionInstalled = await isInstalled();

    if (web.enabled) {
      if (!isHelperExtensionInstalled) {
        setGranted(false, {
          title: 'Easily browse websites here!',
          message: `<i>To embed <b>2100+ tools</b> 🔥 within <span>${
            extension.name || ''
          }</span>, please install Panda Helper extension.</i>`,
          button: {
            label: 'Install Panda Helper Extension',
            async onClick() {
              requestInstall(() => {
                setGranted(true);
              });
            },
          },
          buttonSecondary: {
            label: 'Open link in new tab',
            onClick() {
              window.open(selectedItemUrl, '_blank');
            },
          },
        });
      } else {
        setGranted(true);
      }
    } else if (extension.self) {
      const permissionGranted = await askExtension(
        ExtentionMethod.HAS_PERMISSION,
        'webRequest'
      );

      if (permissionGranted) {
        setGranted(true);
      } else {
        setGranted(false, {
          title: 'Easily browse websites here!',
          message: 'To embed this website we need your optional permission.',
          button: {
            label: `Allow embeding on ${extension.name || 'The Stocks'}`,
            async onClick() {
              const granted = await askExtension(
                ExtentionMethod.REQUEST_PERMISSION,
                'webRequest'
              );

              setGranted(granted);
            },
          },
          buttonSecondary: {
            label: 'Open link in new tab',
            onClick() {
              window.open(selectedItemUrl, '_blank');
            },
          },
        });
      }
    } else {
      setGranted(false, { title: 'Not available!' });
    }
  }
</script>

{#if checked && !granted}
  <div class="permission-request">
    {#if template}
      <!-- <span class="tip"><InlineSvg src="/img/icon/permission.svg" /></span> -->

      <h2>{template.title}</h2>

      {#if template.message}
        <p>{@html template.message}</p>
      {/if}

      <div class="buttons">
        {#if template.button}
          <div class="primary button" role="button" on:click={template.button.onClick}>
            <div class="icon">
              <InlineSvg src="/img/icon/chrome.svg" />
            </div>
            <div class="button-text">
              {template.button.label}
              <span class="subtext"> Available on Chrome Webstore </span>
            </div>
            <span class="new-tab">
              <InlineSvg src="/img/icon/new-tab.svg" />
            </span>
          </div>
        {/if}

        {#if template.buttonSecondary}
          <div
            class="secondary button"
            role="button"
            on:click={template.buttonSecondary.onClick}
          >
            <div class="button-text">
              {template.buttonSecondary.label}
              <span class="subtext">
                {selectedItemHostname}
              </span>
            </div>
            <span class="new-tab">
              <InlineSvg src="/img/icon/new-tab.svg" width="12" />
            </span>
          </div>
        {/if}
      </div>
      <div class="video-container">
        <video bind:playbackRate autoplay loop muted>
          <source src="thestocks.mp4" type="video/mp4" />
        </video>
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  @import '../styles/mixins';

  .permission-request {
    flex: 1;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-image: url('/img/permission-bg-top.svg');
    background-repeat: no-repeat;
    background-position: top center;

    h2 {
      @include font(Semibold);
      font-size: 36px;
      line-height: 120%;
      margin-top: 56px;
      margin-bottom: 16px;
    }

    p {
      @include font();
      font-size: 16px;
      line-height: 21px;
    }

    .video-container {
      max-width: 100%;
      aspect-ratio: 1200/706;
      overflow: hidden;
      margin: 0 48px;
      margin-bottom: -8px;
      video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        margin: 0 auto;
        border: 4px solid rgba(0, 0, 0, 0.1);
        border-radius: 10px;
      }
    }

    // .tip {
    //   position: relative;

    //   &::after {
    //     content: 'Trusted by 15,000,000+ lovely unique creatives every year since 2013 ❤️';
    //     position: absolute;
    //     top: -50%;
    //     left: 100%;
    //     width: 281px;
    //     height: 64px;
    //     background: var(--color-blue-gray--900);
    //     border-radius: 20px 20px 20px 8px;
    //     display: flex;
    //     justify-content: center;
    //     align-items: center;
    //     padding: 14px 17px;
    //     @include font(Semibold);
    //     font-size: 13px;
    //     line-height: 18px;
    //     font-style: italic;
    //     color: var(--color-bg--lightest);
    //   }
    // }

    .buttons {
      display: flex;
      justify-content: space-between;
      .button {
        display: flex;
        background-color: var(--color-green--600);
        position: relative;
        padding: 20px 16px;
        border-radius: 10px;
        margin-top: 24px;
        margin-bottom: 42px;
        gap: 16px;
        transition: all 0.1s ease-in;

        &.secondary {
          background-color: var(--color-bg--lightest);
          margin-left: 21px;
          --svg-stroke: var(--color-blue-gray--700);

          .button-text {
            color: var(--color-blue-gray--700);

            .subtext {
              color: var(--color-blue-gray--500);
            }
          }
        }
        .icon {
          display: inline-flex;
          align-self: center;
        }

        &:hover {
          opacity: 0.85;
        }

        &:active {
          transform: scale(0.98);
        }
      }

      .button-text {
        display: flex;
        flex-direction: column;
        @include font(Semibold);
        font-size: 16px;
        line-height: 150%;
        color: var(--color-text--lightest);
        text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.15);

        .subtext {
          @include font(Semibold);
          color: var(--color-blue-gray--50);
          font-size: 12px;
          line-height: 16px;
          margin-top: 2px;
          opacity: 0.7;
        }
      }

      .new-tab {
        :global(.svg-wrapper) {
          position: absolute;
          top: 8px;
          right: 8px;
        }
      }
    }

    :global(i span) {
      @include font(Medium);
    }

    :global(button.saturated) {
      background-color: var(--color-blue-gray--500);
      color: var(--color-blue-gray--50);
      border: none;
    }
  }

  :global(.dark-mode) {
    .permission-request {
      h2 {
        color: white;
      }

      p {
        color: var(--color-blue-gray--200);
      }

      .button.secondary {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.15);
        --svg-stroke: var(--color-blue-gray--50);

        .button-text {
          color: var(--color-blue-gray--50);

          .subtext {
            color: var(--color-blue-gray--50);
            opacity: 0.7;
          }
        }
      }

      .video-container {
        video {
          border: 4px solid rgba(255, 255, 255, 0.1);
        }
      }
    }

    // .tip {
    //   &::after {
    //     background: var(--color-bg--lightest);
    //     color: var(--color-blue-gray--900);
    //   }
    // }
  }

  :global(.dark-mode .permission-request b, .dark-mode .permission-request span) {
    color: white;
  }

  @media screen and (max-height: 620px) {
    .permission-request .buttons .button {
      margin-top: 32px;
    }
  }
</style>
